import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppShell } from "@/layouts/AppShell";
import { CmsInspectorRoute } from "@/modules/cms/components/CmsInspector";
import { GuestRoute, ProtectedRoute } from "@/routes/guards";
import { Skeleton } from "@/components/ui";

const LoginPage = lazy(() => import("@/modules/auth/LoginPage"));
const DashboardPage = lazy(() => import("@/modules/dashboard/DashboardPage"));
const SettingsLayout = lazy(() => import("@/modules/settings/SettingsLayout"));
const SettingsIndex = lazy(() => import("@/modules/settings/SettingsPage"));
const NotificationsPage = lazy(
  () => import("@/modules/settings/pages/notifications/NotificationsPage")
);
const EmailSmtpPage = lazy(() => import("@/modules/settings/pages/email/EmailSmtpPage"));
const SecurityPage = lazy(() => import("@/modules/settings/pages/security/SecurityPage"));
const StoragePage = lazy(() => import("@/modules/settings/pages/storage/StoragePage"));
const BackupPage = lazy(() => import("@/modules/settings/pages/backup/BackupPage"));
const SystemInfoPage = lazy(
  () => import("@/modules/settings/pages/system-info/SystemInfoPage")
);
const ProfilePage = lazy(() => import("@/modules/account/ProfilePage"));
const UserSettingsPage = lazy(() => import("@/modules/account/UserSettingsPage"));
const AppearancePage = lazy(() => import("@/modules/account/AppearancePage"));
const NotificationPreferencesPage = lazy(
  () => import("@/modules/account/NotificationPreferencesPage")
);
const HelpPage = lazy(() => import("@/modules/account/HelpPage"));
const DownloadAppPage = lazy(() => import("@/modules/download/DownloadAppPage"));
const FinancePage = lazy(() => import("@/modules/finance/FinancePage"));
const FinanceIncomePage = lazy(() => import("@/modules/finance/pages/IncomePage"));
const FinanceExpensesPage = lazy(() => import("@/modules/finance/pages/ExpensesPage"));
const FinanceInvoicesPage = lazy(() => import("@/modules/finance/pages/InvoicesPage"));
const FinanceInvoiceDetailPage = lazy(
  () => import("@/modules/finance/pages/InvoiceDetailPage")
);
const FinanceTransactionsPage = lazy(
  () => import("@/modules/finance/pages/TransactionsPage")
);
const FinanceCashFlowPage = lazy(() => import("@/modules/finance/pages/CashFlowPage"));
const FinanceReportsPage = lazy(
  () => import("@/modules/finance/pages/FinanceReportsPage")
);
const FinanceSettingsPage = lazy(
  () => import("@/modules/finance/pages/FinanceSettingsPage")
);
const CmsPage = lazy(() => import("@/modules/cms/CmsPage"));
const CmsHomepagePage = lazy(() => import("@/modules/cms/pages/HomepagePage"));
const CmsPortfolioPage = lazy(() => import("@/modules/cms/pages/PortfolioPage"));
const CmsPortfolioEditorPage = lazy(() => import("@/modules/cms/pages/PortfolioEditorPage"));
const CmsFounderPage = lazy(() => import("@/modules/cms/pages/FounderPage"));
const CmsCompanyInfoPage = lazy(() => import("@/modules/cms/pages/CompanyInfoPage"));
const CmsSocialMediaPage = lazy(() => import("@/modules/cms/pages/SocialMediaPage"));
const CmsVisitorAnalyticsPage = lazy(() => import("@/modules/cms/pages/VisitorAnalyticsPage"));
const GrowthStrategyPage = lazy(() => import("@/modules/growth-strategy/pages/GrowthStrategyPage"));
const IntegrationsPage = lazy(() => import("@/modules/integrations/IntegrationsPage"));

const BdDashboardPage = lazy(
  () => import("@/modules/business-development/pages/dashboard/BdDashboardPage")
);
const InquiriesPage = lazy(
  () => import("@/modules/business-development/pages/inquiries/InquiriesPage")
);
const InquiryDetailPage = lazy(
  () => import("@/modules/business-development/pages/inquiries/InquiryDetailPage")
);
const ApplicationsPage = lazy(
  () => import("@/modules/business-development/pages/applications/ApplicationsPage")
);
const ApplicationDetailPage = lazy(
  () => import("@/modules/business-development/pages/applications/ApplicationDetailPage")
);
const AnnouncementsPage = lazy(
  () => import("@/modules/business-development/pages/announcements/AnnouncementsPage")
);
const AnnouncementEditorPage = lazy(
  () => import("@/modules/business-development/pages/announcements/AnnouncementEditorPage")
);
const AnnouncementSubmissionsPage = lazy(
  () => import("@/modules/business-development/pages/announcements/AnnouncementSubmissionsPage")
);
const CrmPage = lazy(() => import("@/modules/business-development/pages/crm/CrmPage"));
const CrmProjectsPage = lazy(
  () => import("@/modules/business-development/pages/crm/projects/ProjectsPage")
);
const CrmClientsPage = lazy(
  () => import("@/modules/business-development/pages/crm/clients/ClientsPage")
);
const CrmClientDetailPage = lazy(
  () => import("@/modules/business-development/pages/crm/clients/ClientDetailPage")
);
const CrmClientProjectProfilePage = lazy(
  () => import("@/modules/business-development/pages/crm/clients/ClientProjectProfilePage")
);
const LeadsPage = lazy(() => import("@/modules/business-development/pages/crm/LeadsPage"));
const LeadDetailPage = lazy(
  () => import("@/modules/business-development/pages/crm/LeadDetailPage")
);
const CompanyProfilePage = lazy(
  () => import("@/modules/business-development/pages/crm/CompanyProfilePage")
);
const ContactProfilePage = lazy(
  () => import("@/modules/business-development/pages/crm/ContactProfilePage")
);
const OutreachDashboardPage = lazy(
  () => import("@/modules/business-development/pages/outreach/OutreachDashboardPage")
);
const CampaignsPage = lazy(
  () => import("@/modules/business-development/pages/outreach/CampaignsPage")
);
const CampaignDetailPage = lazy(
  () => import("@/modules/business-development/pages/outreach/CampaignDetailPage")
);
const TemplatesPage = lazy(
  () => import("@/modules/business-development/pages/outreach/TemplatesPage")
);
const TemplateBuilderPage = lazy(
  () => import("@/modules/business-development/pages/outreach/TemplateBuilderPage")
);
const AudiencePage = lazy(
  () => import("@/modules/business-development/pages/outreach/AudiencePage")
);
const AudienceImportPage = lazy(
  () => import("@/modules/business-development/pages/outreach/AudienceImportPage")
);
const AudienceDetailPage = lazy(
  () => import("@/modules/business-development/pages/outreach/AudienceDetailPage")
);
const CreateCampaignPage = lazy(
  () => import("@/modules/business-development/pages/outreach/CreateCampaignPage")
);
const SuccessfulLeadsPage = lazy(
  () => import("@/modules/business-development/pages/outreach/SuccessfulLeadsPage")
);
const DeadLeadsPage = lazy(
  () => import("@/modules/business-development/pages/outreach/DeadLeadsPage")
);
const SuccessfulLeadsDetailPage = lazy(
  () => import("@/modules/business-development/pages/outreach/SuccessfulLeadsDetailPage")
);
const DeadLeadsDetailPage = lazy(
  () => import("@/modules/business-development/pages/outreach/DeadLeadsDetailPage")
);
const OutreachAnalyticsPage = lazy(
  () => import("@/modules/business-development/pages/outreach/OutreachAnalyticsPage")
);
const MeetingsLayout = lazy(() => import("@/modules/meetings/components/MeetingsLayout"));
const MeetingsDashboardPage = lazy(() => import("@/modules/meetings/pages/MeetingsDashboardPage"));
const MeetingsCalendarPage = lazy(() => import("@/modules/meetings/pages/MeetingsCalendarPage"));
const UpcomingMeetingsPage = lazy(() => import("@/modules/meetings/pages/UpcomingMeetingsPage"));
const InternalMeetingsPage = lazy(() => import("@/modules/meetings/pages/InternalMeetingsPage"));
const ClientMeetingsPage = lazy(() => import("@/modules/meetings/pages/ClientMeetingsPage"));
const SalesMeetingsPage = lazy(() => import("@/modules/meetings/pages/SalesMeetingsPage"));
const ProjectMeetingsPage = lazy(() => import("@/modules/meetings/pages/ProjectMeetingsPage"));
const InterviewsPage = lazy(() => import("@/modules/meetings/pages/InterviewsPage"));
const MeetingNotesPage = lazy(() => import("@/modules/meetings/pages/MeetingNotesPage"));
const RecordingsPage = lazy(() => import("@/modules/meetings/pages/RecordingsPage"));
const FollowUpsPage = lazy(() => import("@/modules/meetings/pages/FollowUpsPage"));
const ActionItemsPage = lazy(() => import("@/modules/meetings/pages/ActionItemsPage"));
const MeetingTemplatesPage = lazy(() => import("@/modules/meetings/pages/TemplatesPage"));
const MeetingsAnalyticsPage = lazy(() => import("@/modules/meetings/pages/MeetingsAnalyticsPage"));
const MeetingDetailPage = lazy(() => import("@/modules/meetings/pages/MeetingDetailPage"));
const TasksPage = lazy(
  () => import("@/modules/business-development/pages/tasks/TasksPage")
);
const TaskDetailPage = lazy(
  () => import("@/modules/business-development/pages/tasks/TaskDetailPage")
);
const DocumentsHubPage = lazy(
  () => import("@/modules/business-development/pages/documents/DocumentsHubPage")
);
const CostCalculatorPage = lazy(
  () => import("@/modules/business-development/pages/documents/CostCalculatorPage")
);
const PricingLibraryPage = lazy(
  () => import("@/modules/business-development/pages/documents/PricingLibraryPage")
);
const QuotationsPage = lazy(
  () => import("@/modules/business-development/pages/documents/QuotationsPage")
);
const QuotationCreatePage = lazy(
  () => import("@/modules/business-development/pages/documents/QuotationCreatePage")
);
const QuotationDetailPage = lazy(
  () => import("@/modules/business-development/pages/documents/QuotationDetailPage")
);
const ProposalsPage = lazy(
  () => import("@/modules/business-development/pages/documents/ProposalsPage")
);
const ProposalBuilderPage = lazy(
  () => import("@/modules/business-development/pages/documents/ProposalBuilderPage")
);
const ProposalDetailPage = lazy(
  () => import("@/modules/business-development/pages/documents/ProposalDetailPage")
);
const ContractsPage = lazy(
  () => import("@/modules/business-development/pages/documents/ContractsPage")
);
const ContractDetailPage = lazy(
  () => import("@/modules/business-development/pages/documents/ContractDetailPage")
);
const DocTemplatesPage = lazy(
  () => import("@/modules/business-development/pages/documents/DocTemplatesPage")
);
const ReportsPage = lazy(
  () => import("@/modules/business-development/pages/reports/ReportsPage")
);

function LegacyMeetingRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/meetings/${id}` : "/meetings"} replace />;
}

function PageFallback() {
  return (
    <div style={{ display: "grid", gap: "0.75rem", padding: "1rem 0" }}>
      <Skeleton height={28} width="30%" />
      <Skeleton height={160} />
      <Skeleton height={220} />
    </div>
  );
}

function LegacyOutreachRedirect() {
  const { pathname } = useLocation();
  const rest = pathname.replace(/^\/business-development\/outreach\/?/, "");
  return <Navigate to={`/sales-engine${rest ? `/${rest}` : ""}`} replace />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />

            <Route path="system-settings" element={<SettingsLayout />}>
              <Route index element={<SettingsIndex />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="email" element={<EmailSmtpPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="storage" element={<StoragePage />} />
              <Route path="backup" element={<BackupPage />} />
              <Route path="system" element={<SystemInfoPage />} />
            </Route>

            {/* Compatibility redirects from previous /settings paths */}
            <Route path="settings/*" element={<Navigate to="/system-settings" replace />} />

            <Route path="profile" element={<ProfilePage />} />
            <Route path="user-settings" element={<UserSettingsPage />} />
            <Route path="appearance" element={<AppearancePage />} />
            <Route
              path="notification-preferences"
              element={<NotificationPreferencesPage />}
            />
            <Route path="help" element={<HelpPage />} />
            <Route path="download-app" element={<DownloadAppPage />} />

            {/* Business Development */}
            <Route path="business-development" element={<BdDashboardPage />} />
            <Route path="business-development/inquiries" element={<InquiriesPage />} />
            <Route
              path="business-development/inquiries/:id"
              element={<InquiryDetailPage />}
            />
            <Route
              path="business-development/applications"
              element={<ApplicationsPage />}
            />
            <Route
              path="business-development/applications/:id"
              element={<ApplicationDetailPage />}
            />
            <Route
              path="business-development/announcements"
              element={<AnnouncementsPage />}
            />
            <Route
              path="business-development/announcements/submissions"
              element={<AnnouncementSubmissionsPage />}
            />
            <Route
              path="business-development/announcements/new"
              element={<AnnouncementEditorPage key="new" />}
            />
            <Route
              path="business-development/announcements/:id"
              element={<AnnouncementEditorPage />}
            />
            <Route path="business-development/crm" element={<CrmPage />} />
            <Route path="business-development/crm/leads" element={<LeadsPage />} />
            <Route path="business-development/crm/clients" element={<CrmClientsPage />} />
            <Route
              path="business-development/crm/clients/:id"
              element={<CrmClientDetailPage />}
            />
            <Route
              path="business-development/crm/clients/:id/projects/:projectId"
              element={<CrmClientProjectProfilePage />}
            />
            <Route path="business-development/crm/projects" element={<CrmProjectsPage />} />
            <Route
              path="business-development/crm/projects/current"
              element={<Navigate to="/business-development/crm/projects" replace />}
            />
            <Route
              path="business-development/crm/projects/pending"
              element={<Navigate to="/business-development/crm/projects" replace />}
            />
            <Route
              path="business-development/crm/projects/upcoming"
              element={<Navigate to="/business-development/crm/projects" replace />}
            />
            <Route
              path="business-development/crm/projects/completed"
              element={<Navigate to="/business-development/crm/projects" replace />}
            />
            <Route
              path="business-development/crm/maintenance"
              element={<Navigate to="/business-development/crm/clients" replace />}
            />
            <Route
              path="business-development/crm/deliverables"
              element={<Navigate to="/business-development/crm/projects" replace />}
            />
            <Route
              path="business-development/crm/renewals"
              element={<Navigate to="/business-development/crm/clients" replace />}
            />
            <Route
              path="business-development/crm/leads/:id"
              element={<LeadDetailPage />}
            />
            <Route
              path="business-development/crm/companies/:id"
              element={<CompanyProfilePage />}
            />
            <Route
              path="business-development/crm/contacts/:id"
              element={<ContactProfilePage />}
            />
            <Route path="sales-engine" element={<OutreachDashboardPage />} />
            <Route path="sales-engine/campaigns" element={<CampaignsPage />} />
            <Route path="sales-engine/campaigns/new" element={<CreateCampaignPage />} />
            <Route path="sales-engine/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="sales-engine/templates" element={<TemplatesPage />} />
            <Route path="sales-engine/templates/new" element={<TemplateBuilderPage />} />
            <Route path="sales-engine/templates/:id" element={<TemplateBuilderPage />} />
            <Route path="sales-engine/audience/import" element={<AudienceImportPage />} />
            <Route path="sales-engine/audience/:id" element={<AudienceDetailPage />} />
            <Route path="sales-engine/audience" element={<AudiencePage />} />
            <Route
              path="sales-engine/successful-leads/:campaignId"
              element={<SuccessfulLeadsDetailPage />}
            />
            <Route path="sales-engine/successful-leads" element={<SuccessfulLeadsPage />} />
            <Route
              path="sales-engine/dead-leads/:campaignId"
              element={<DeadLeadsDetailPage />}
            />
            <Route path="sales-engine/dead-leads" element={<DeadLeadsPage />} />
            <Route path="sales-engine/analytics" element={<OutreachAnalyticsPage />} />
            <Route path="business-development/outreach/*" element={<LegacyOutreachRedirect />} />
            <Route path="business-development/meetings" element={<Navigate to="/meetings" replace />} />
            <Route path="business-development/meetings/:id" element={<LegacyMeetingRedirect />} />
            <Route path="business-development/tasks" element={<TasksPage />} />
            <Route path="business-development/tasks/:id" element={<TaskDetailPage />} />
            <Route
              path="business-development/documents"
              element={<DocumentsHubPage />}
            />
            <Route
              path="business-development/documents/calculator"
              element={<CostCalculatorPage />}
            />
            <Route
              path="business-development/documents/pricing-library"
              element={<PricingLibraryPage />}
            />
            <Route
              path="business-development/documents/quotations"
              element={<QuotationsPage />}
            />
            <Route
              path="business-development/documents/quotations/new"
              element={<QuotationCreatePage />}
            />
            <Route
              path="business-development/documents/quotations/:id"
              element={<QuotationDetailPage />}
            />
            <Route
              path="business-development/documents/proposals"
              element={<ProposalsPage />}
            />
            <Route
              path="business-development/documents/proposals/new"
              element={<ProposalBuilderPage />}
            />
            <Route
              path="business-development/documents/proposals/:id/edit"
              element={<ProposalBuilderPage />}
            />
            <Route
              path="business-development/documents/proposals/:id"
              element={<ProposalDetailPage />}
            />
            <Route
              path="business-development/documents/contracts"
              element={<ContractsPage />}
            />
            <Route
              path="business-development/documents/contracts/:id"
              element={<ContractDetailPage />}
            />
            <Route
              path="business-development/documents/templates"
              element={<DocTemplatesPage />}
            />
            <Route path="business-development/reports" element={<ReportsPage />} />

            {/* Legacy acquisition → BD */}
            <Route
              path="acquisition"
              element={<Navigate to="/business-development" replace />}
            />
            <Route
              path="acquisition/*"
              element={<Navigate to="/business-development" replace />}
            />

            <Route path="finance" element={<FinancePage />} />
            <Route path="finance/income" element={<FinanceIncomePage />} />
            <Route path="finance/expenses" element={<FinanceExpensesPage />} />
            <Route path="finance/invoices" element={<FinanceInvoicesPage />} />
            <Route path="finance/invoices/:id" element={<FinanceInvoiceDetailPage />} />
            <Route path="finance/transactions" element={<FinanceTransactionsPage />} />
            <Route path="finance/cash-flow" element={<FinanceCashFlowPage />} />
            <Route path="finance/reports" element={<FinanceReportsPage />} />
            <Route path="finance/settings" element={<FinanceSettingsPage />} />
            <Route
              path="clients"
              element={<Navigate to="/business-development/crm/clients" replace />}
            />
            <Route path="cms" element={<CmsInspectorRoute />}>
              <Route index element={<CmsPage />} />
              <Route path="homepage" element={<CmsHomepagePage />} />
              <Route path="portfolio" element={<CmsPortfolioPage />} />
              <Route path="portfolio/new" element={<CmsPortfolioEditorPage />} />
              <Route path="portfolio/:id" element={<CmsPortfolioEditorPage />} />
              <Route path="founder" element={<CmsFounderPage />} />
              <Route path="company" element={<CmsCompanyInfoPage />} />
              <Route path="social" element={<CmsSocialMediaPage />} />
              <Route path="analytics" element={<CmsVisitorAnalyticsPage />} />
            </Route>
            <Route path="analytics" element={<Navigate to="/growth-strategy" replace />} />
            <Route path="growth-strategy" element={<GrowthStrategyPage />} />
            <Route path="meetings" element={<MeetingsLayout />}>
              <Route index element={<MeetingsDashboardPage />} />
              <Route path="calendar" element={<MeetingsCalendarPage />} />
              <Route path="upcoming" element={<UpcomingMeetingsPage />} />
              <Route path="internal" element={<InternalMeetingsPage />} />
              <Route path="clients" element={<ClientMeetingsPage />} />
              <Route path="sales" element={<SalesMeetingsPage />} />
              <Route path="projects" element={<ProjectMeetingsPage />} />
              <Route path="interviews" element={<InterviewsPage />} />
              <Route path="notes" element={<MeetingNotesPage />} />
              <Route path="recordings" element={<RecordingsPage />} />
              <Route path="follow-ups" element={<FollowUpsPage />} />
              <Route path="action-items" element={<ActionItemsPage />} />
              <Route path="templates" element={<MeetingTemplatesPage />} />
              <Route path="analytics" element={<MeetingsAnalyticsPage />} />
              <Route path=":id" element={<MeetingDetailPage />} />
            </Route>
            <Route path="integrations" element={<IntegrationsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
